import { useEffect, useState } from 'react';
import {
  Container,
  Box,
  ThemeProvider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FeedbackIcon from '@mui/icons-material/Feedback';
import NavBar from '../../layout/NavBar';
import NavBarBot from '../../layout/NavBarBot';
import { TrainingApi, GruppenApi } from '../../../services/api';
import { useAuth } from '../../context/AuthContext';
import { darkTheme } from '../../../theme/darkTheme';
import { Navigate } from 'react-router-dom';
import GymKalenderWidget from '../../shared/GurppenKalenderWidget';
import HeaderCard from '../../layout/HeaderCard';
import Highscores from '../features/Highscores';
import LoadingPage from '../../layout/LoadingPage';
import AnsichtToggle from './components/home/AnsichtToggle';
import EinladungenCard from './components/home/EinladungenCard';
import DashboardCard from './components/home/DashboardCard';
import LetzteTrainingsCard from './components/home/LetzteTrainingsCard';

function HomeDark() {
  const navigate = useNavigate();
  const { nutzer, isLoggedIn, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [favoritGruppe, setFavoritGruppe] = useState(null);
  const [ansicht, setAnsicht] = useState('gruppe');
  const [loading, setLoading] = useState(true);
  const [einladungen, setEinladungen] = useState([]);
  const [gruppeId, setGruppeId] = useState("");
  const [, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!isLoggedIn || !nutzer?.id) {
      setLoading(false);
      return;
    }
    fetchFavoritGruppe();
  }, [nutzer, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !nutzer?.id) {
      setLoading(false);
      return;
    }
    fetchStats();
    fetchEinladungen();
  }, [nutzer, isLoggedIn, ansicht, favoritGruppe]);

  useEffect(() => {
    setGruppeId(ansicht === 'gruppe' && favoritGruppe?.id ? favoritGruppe.id : null);
  }, [ansicht, favoritGruppe]);

  const fetchFavoritGruppe = async () => {
    setLoading(true);
    try {
      const response = await GruppenApi.getFavoritGruppe(nutzer.id);
      setFavoritGruppe(response.data || null);
      if (!response.data) {
        setAnsicht('persoenlich');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Favoriten-Gruppe:', err);
      setMessage({ type: "error", text: 'Favoriten-Gruppe konnte nicht geladen werden' });

    } finally {
      setLoading(false);
    }
  };

  const fetchEinladungen = async () => {
    setLoading(true)
    try {
      const response = await GruppenApi.getEinladungen(nutzer.id);
      setEinladungen(response.data);
    } catch (err) {
      setMessage({ type: "error", text: 'Fehler beim abrufen der Einladungen' });

      console.error("Fetch Fehler Einladungen: ", err);
    } finally {
      setLoading(false);
    }
  }

  const fetchStats = async () => {
    setLoading(true);
    try {
      const gruppeId = ansicht === 'gruppe' && favoritGruppe?.id ? favoritGruppe.id : null;
      const response = await TrainingApi.getDashboardStats(nutzer.id, gruppeId ? { gruppeId } : {});

      const letzteTrainings = Array.isArray(response.data?.letzteTrainings)
        ? response.data.letzteTrainings.filter((t) => t && t.id)
        : [];

      const verbesserungen = Array.isArray(response.data?.verbesserungen)
        ? response.data.verbesserungen.filter((v) => v && v.uebung_name)
        : [];

      // Accept any truthy highscores items (server may omit `id`); keep entries for display
      const highscores = Array.isArray(response.data?.highscores)
        ? response.data.highscores.filter((h) => !!h)
        : [];

      setStats({ letzteTrainings, verbesserungen, highscores });
    } catch (err) {
      console.error('Fehler beim Laden der Statistiken:', err);
      setMessage({ type: "error", text: 'Statistiken konnten nicht geladen werden' });

    } finally {
      setLoading(false);
    }
  };

  const handleAnsichtChange = (event, newAnsicht) => {
    if (newAnsicht !== null) {
      setAnsicht(newAnsicht);
    }
  };

  const handleOpenTraining = () => {
    navigate("/addTraining");
  }

  const handleOpenFeedback = () => {
    navigate("/feedback");
  }

  if (authLoading) {
    return (
      <>
        <LoadingPage />
      </>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <LoadingPage />
    );
  }

  return (
    <>
      <ThemeProvider theme={darkTheme}>
        <Box
          sx={{
            bgcolor: 'background.default',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            pb: 4,
          }}
        >
          <NavBar />
          <Container
            maxWidth="lg"
            sx={{
              pt: { xs: 2, md: 4 },
              px: { xs: 1, sm: 2 },
              flexGrow: 1,
              pb: '64px',
            }}
          >
            {favoritGruppe && (
              <AnsichtToggle ansicht={ansicht} onChange={handleAnsichtChange} />
            )}
            <HeaderCard title={`Willkommen zurück, ${nutzer?.vname}`} />

            {einladungen.length > 0 && (
              <EinladungenCard einladungen={einladungen} onOpen={() => navigate("/einladungen")} />
            )}
            {favoritGruppe && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    minHeight: { xs: '200px', sm: '400px' },
                    borderRadius: '16px',
                    overflow: 'hidden',
                  }}
                >
                  <GymKalenderWidget
                    gruppeId={favoritGruppe.id}
                    showAddButton={true}
                    showTerminList={true}
                    compact={false}
                    sx={{ width: '100%', height: '100%' }}
                  />
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <DashboardCard
                title="Letzte Trainings"
                icon={<CalendarTodayIcon fontSize="small" />}
                color="#1e3a8a"
                hoverColor="#3b82f6"
              >
                <LetzteTrainingsCard
                  trainings={stats?.letzteTrainings}
                  ansicht={ansicht}
                  nutzer={nutzer}
                  navigate={navigate}
                />
              </DashboardCard>
              <DashboardCard
                title={`Highscores ${ansicht === 'gruppe' ? '(Gruppe)' : ''}`}
                icon={<EmojiEventsIcon fontSize="small" />}
                color="#1e3a8a"
                hoverColor="#3b82f6"
              >
                <Highscores highscores={stats?.highscores} ansicht={ansicht} nutzer={nutzer} navigate={navigate} gruppeId={gruppeId} />
              </DashboardCard>
            </Box>
          </Container>
        </Box>
      </ThemeProvider>
      <NavBarBot mainBtnF={handleOpenTraining} mainBtnTxt={"Start"} sideBtn1Icon={<FeedbackIcon />} sideBtn1F={handleOpenFeedback} />
    </>
  );
}

export default HomeDark;
